import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { paths } from '../firebase/paths';
import type {
  Project,
  Sprint,
  Task,
  Assignee,
  DailyGoal,
  CallTopic,
  Refinement,
  ScopeItem,
} from '../types/models';

// -------- helpers --------

const tsToMs = (v: unknown): number => {
  if (v instanceof Timestamp) return v.toMillis();
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object' && 'seconds' in (v as any)) {
    return (v as { seconds: number }).seconds * 1000;
  }
  return Date.now();
};

const mapDoc = <T>(snap: { id: string; data: () => any }): T =>
  ({
    id: snap.id,
    ...snap.data(),
  }) as T;

interface Ctx {
  uid: string;
}
interface SprintCtx extends Ctx {
  projectId: string;
}
interface TaskCtx extends SprintCtx {
  sprintId: string;
}

// -------- API --------

export const firestoreApi = createApi({
  reducerPath: 'firestoreApi',
  baseQuery: fakeBaseQuery<string>(),
  tagTypes: [
    'Project',
    'Sprint',
    'Task',
    'Assignee',
    'DailyGoal',
    'CallTopic',
    'Refinement',
    'ScopeItem',
  ],
  endpoints: (build) => ({
    // =================================================================
    // Projects
    // =================================================================
    listProjects: build.query<Project[], Ctx>({
      async queryFn({ uid }) {
        try {
          const q = query(
            collection(db, paths.projects(uid)),
            orderBy('createdDate', 'desc'),
          );
          const snap = await getDocs(q);
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                ...data,
                createdDate: tsToMs(data.createdDate),
              } as Project;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listProjects failed' };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: 'Project' as const, id: p.id })),
              { type: 'Project' as const, id: 'LIST' },
            ]
          : [{ type: 'Project' as const, id: 'LIST' }],
    }),

    createProject: build.mutation<
      Project,
      Ctx & Omit<Project, 'id' | 'createdBy' | 'createdDate'>
    >({
      async queryFn({ uid, ...rest }) {
        try {
          const ref = await addDoc(collection(db, paths.projects(uid)), {
            ...rest,
            createdBy: uid,
            createdDate: serverTimestamp(),
          });
          return {
            data: {
              id: ref.id,
              ...rest,
              createdBy: uid,
              createdDate: Date.now(),
            } as Project,
          };
        } catch (e: any) {
          return { error: e?.message ?? 'createProject failed' };
        }
      },
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    updateProject: build.mutation<
      void,
      Ctx & { id: string; patch: Partial<Project> }
    >({
      async queryFn({ uid, id, patch }) {
        try {
          await updateDoc(doc(db, paths.project(uid, id)), patch as any);
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'updateProject failed' };
        }
      },
      invalidatesTags: (_r, _e, a) => [
        { type: 'Project', id: a.id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    deleteProject: build.mutation<void, Ctx & { id: string }>({
      async queryFn({ uid, id }) {
        try {
          await deleteDoc(doc(db, paths.project(uid, id)));
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteProject failed' };
        }
      },
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    // =================================================================
    // Sprints
    // =================================================================
    listSprints: build.query<Sprint[], SprintCtx>({
      async queryFn({ uid, projectId }) {
        try {
          const q = query(
            collection(db, paths.sprints(uid, projectId)),
            orderBy('createdDate', 'desc'),
          );
          const snap = await getDocs(q);
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                projectId,
                ...data,
                createdDate: tsToMs(data.createdDate),
              } as Sprint;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listSprints failed' };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: 'Sprint' as const, id: s.id })),
              { type: 'Sprint' as const, id: 'LIST' },
            ]
          : [{ type: 'Sprint' as const, id: 'LIST' }],
    }),

    createSprint: build.mutation<
      Sprint,
      SprintCtx & Omit<Sprint, 'id' | 'projectId' | 'createdDate'>
    >({
      async queryFn({ uid, projectId, ...rest }) {
        try {
          const ref = await addDoc(
            collection(db, paths.sprints(uid, projectId)),
            { ...rest, createdDate: serverTimestamp() },
          );
          return {
            data: {
              id: ref.id,
              projectId,
              ...rest,
              createdDate: Date.now(),
            } as Sprint,
          };
        } catch (e: any) {
          return { error: e?.message ?? 'createSprint failed' };
        }
      },
      invalidatesTags: [{ type: 'Sprint', id: 'LIST' }],
    }),

    updateSprint: build.mutation<
      void,
      SprintCtx & { id: string; patch: Partial<Sprint> }
    >({
      async queryFn({ uid, projectId, id, patch }) {
        try {
          await updateDoc(doc(db, paths.sprint(uid, projectId, id)), patch as any);
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'updateSprint failed' };
        }
      },
      invalidatesTags: (_r, _e, a) => [
        { type: 'Sprint', id: a.id },
        { type: 'Sprint', id: 'LIST' },
      ],
    }),

    deleteSprint: build.mutation<void, SprintCtx & { id: string }>({
      async queryFn({ uid, projectId, id }) {
        try {
          await deleteDoc(doc(db, paths.sprint(uid, projectId, id)));
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteSprint failed' };
        }
      },
      invalidatesTags: [{ type: 'Sprint', id: 'LIST' }],
    }),

    // =================================================================
    // Tasks
    // =================================================================
    listTasks: build.query<Task[], TaskCtx>({
      async queryFn({ uid, projectId, sprintId }) {
        try {
          const q = query(
            collection(db, paths.tasks(uid, projectId, sprintId)),
            orderBy('updatedDate', 'desc'),
          );
          const snap = await getDocs(q);
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                projectId,
                sprintId,
                ...data,
                createdDate: tsToMs(data.createdDate),
                updatedDate: tsToMs(data.updatedDate),
              } as Task;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listTasks failed' };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((t) => ({ type: 'Task' as const, id: t.id })),
              { type: 'Task' as const, id: 'LIST' },
            ]
          : [{ type: 'Task' as const, id: 'LIST' }],
    }),

    createTask: build.mutation<
      Task,
      TaskCtx & Omit<Task, 'id' | 'projectId' | 'sprintId' | 'createdDate' | 'updatedDate'>
    >({
      async queryFn({ uid, projectId, sprintId, ...rest }) {
        try {
          const ref = await addDoc(
            collection(db, paths.tasks(uid, projectId, sprintId)),
            {
              ...rest,
              createdDate: serverTimestamp(),
              updatedDate: serverTimestamp(),
            },
          );
          return {
            data: {
              id: ref.id,
              projectId,
              sprintId,
              ...rest,
              createdDate: Date.now(),
              updatedDate: Date.now(),
            } as Task,
          };
        } catch (e: any) {
          return { error: e?.message ?? 'createTask failed' };
        }
      },
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),

    updateTask: build.mutation<
      void,
      TaskCtx & { id: string; patch: Partial<Task> }
    >({
      async queryFn({ uid, projectId, sprintId, id, patch }) {
        try {
          await updateDoc(
            doc(db, paths.task(uid, projectId, sprintId, id)),
            { ...patch, updatedDate: serverTimestamp() } as any,
          );
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'updateTask failed' };
        }
      },
      invalidatesTags: (_r, _e, a) => [
        { type: 'Task', id: a.id },
        { type: 'Task', id: 'LIST' },
      ],
    }),

    deleteTask: build.mutation<void, TaskCtx & { id: string }>({
      async queryFn({ uid, projectId, sprintId, id }) {
        try {
          await deleteDoc(doc(db, paths.task(uid, projectId, sprintId, id)));
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteTask failed' };
        }
      },
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),

    bulkImportTasks: build.mutation<
      number,
      TaskCtx & {
        tasks: Array<Omit<Task, 'id' | 'projectId' | 'sprintId' | 'createdDate' | 'updatedDate'>>;
      }
    >({
      async queryFn({ uid, projectId, sprintId, tasks }) {
        try {
          // Firestore batch caps at 500 ops — chunk if needed.
          const chunks: typeof tasks[] = [];
          for (let i = 0; i < tasks.length; i += 450) {
            chunks.push(tasks.slice(i, i + 450));
          }
          let total = 0;
          for (const chunk of chunks) {
            const batch = writeBatch(db);
            for (const t of chunk) {
              const ref = doc(collection(db, paths.tasks(uid, projectId, sprintId)));
              batch.set(ref, {
                ...t,
                createdDate: serverTimestamp(),
                updatedDate: serverTimestamp(),
              });
            }
            await batch.commit();
            total += chunk.length;
          }
          return { data: total };
        } catch (e: any) {
          return { error: e?.message ?? 'bulkImportTasks failed' };
        }
      },
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),

    // =================================================================
    // Assignees
    // =================================================================
    listAssignees: build.query<Assignee[], SprintCtx>({
      async queryFn({ uid, projectId }) {
        try {
          const snap = await getDocs(
            collection(db, paths.assignees(uid, projectId)),
          );
          return {
            data: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Assignee),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listAssignees failed' };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({ type: 'Assignee' as const, id: a.id })),
              { type: 'Assignee' as const, id: 'LIST' },
            ]
          : [{ type: 'Assignee' as const, id: 'LIST' }],
    }),

    upsertAssignee: build.mutation<
      Assignee,
      SprintCtx & Partial<Assignee> & { name: string; color: string }
    >({
      async queryFn({ uid, projectId, id, ...rest }) {
        try {
          if (id) {
            await setDoc(
              doc(db, paths.assignee(uid, projectId, id)),
              { ...rest, projectId } as any,
              { merge: true },
            );
            return { data: { id, projectId, ...(rest as any) } as Assignee };
          } else {
            const ref = await addDoc(
              collection(db, paths.assignees(uid, projectId)),
              { ...rest, projectId } as any,
            );
            return { data: { id: ref.id, projectId, ...(rest as any) } as Assignee };
          }
        } catch (e: any) {
          return { error: e?.message ?? 'upsertAssignee failed' };
        }
      },
      invalidatesTags: [{ type: 'Assignee', id: 'LIST' }],
    }),

    deleteAssignee: build.mutation<void, SprintCtx & { id: string }>({
      async queryFn({ uid, projectId, id }) {
        try {
          await deleteDoc(doc(db, paths.assignee(uid, projectId, id)));
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteAssignee failed' };
        }
      },
      invalidatesTags: [{ type: 'Assignee', id: 'LIST' }],
    }),

    // =================================================================
    // Daily Goals
    // =================================================================
    listDailyGoals: build.query<DailyGoal[], TaskCtx>({
      async queryFn({ uid, projectId, sprintId }) {
        try {
          const snap = await getDocs(
            collection(db, paths.dailyGoals(uid, projectId, sprintId)),
          );
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                sprintId,
                ...data,
                createdDate: tsToMs(data.createdDate),
              } as DailyGoal;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listDailyGoals failed' };
        }
      },
      providesTags: [{ type: 'DailyGoal', id: 'LIST' }],
    }),

    upsertDailyGoal: build.mutation<
      DailyGoal,
      TaskCtx & Partial<DailyGoal> & { assignee: string; goal: string; date: string }
    >({
      async queryFn({ uid, projectId, sprintId, id, ...rest }) {
        try {
          if (id) {
            await setDoc(
              doc(db, `${paths.dailyGoals(uid, projectId, sprintId)}/${id}`),
              rest as any,
              { merge: true },
            );
            return { data: { id, sprintId, ...(rest as any), createdDate: Date.now() } as DailyGoal };
          } else {
            const ref = await addDoc(
              collection(db, paths.dailyGoals(uid, projectId, sprintId)),
              { ...rest, createdDate: serverTimestamp() } as any,
            );
            return { data: { id: ref.id, sprintId, ...(rest as any), createdDate: Date.now() } as DailyGoal };
          }
        } catch (e: any) {
          return { error: e?.message ?? 'upsertDailyGoal failed' };
        }
      },
      invalidatesTags: [{ type: 'DailyGoal', id: 'LIST' }],
    }),

    deleteDailyGoal: build.mutation<void, TaskCtx & { id: string }>({
      async queryFn({ uid, projectId, sprintId, id }) {
        try {
          await deleteDoc(
            doc(db, `${paths.dailyGoals(uid, projectId, sprintId)}/${id}`),
          );
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteDailyGoal failed' };
        }
      },
      invalidatesTags: [{ type: 'DailyGoal', id: 'LIST' }],
    }),

    // =================================================================
    // Call Topics
    // =================================================================
    listCallTopics: build.query<CallTopic[], TaskCtx>({
      async queryFn({ uid, projectId, sprintId }) {
        try {
          const snap = await getDocs(
            collection(db, paths.callTopics(uid, projectId, sprintId)),
          );
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                sprintId,
                ...data,
                createdDate: tsToMs(data.createdDate),
              } as CallTopic;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listCallTopics failed' };
        }
      },
      providesTags: [{ type: 'CallTopic', id: 'LIST' }],
    }),

    upsertCallTopic: build.mutation<
      CallTopic,
      TaskCtx & Partial<CallTopic> & { topic: string; callType: '4PM' | '8PM'; date: string }
    >({
      async queryFn({ uid, projectId, sprintId, id, ...rest }) {
        try {
          if (id) {
            await setDoc(
              doc(db, `${paths.callTopics(uid, projectId, sprintId)}/${id}`),
              rest as any,
              { merge: true },
            );
            return { data: { id, sprintId, ...(rest as any), createdDate: Date.now() } as CallTopic };
          } else {
            const ref = await addDoc(
              collection(db, paths.callTopics(uid, projectId, sprintId)),
              { ...rest, createdDate: serverTimestamp() } as any,
            );
            return { data: { id: ref.id, sprintId, ...(rest as any), createdDate: Date.now() } as CallTopic };
          }
        } catch (e: any) {
          return { error: e?.message ?? 'upsertCallTopic failed' };
        }
      },
      invalidatesTags: [{ type: 'CallTopic', id: 'LIST' }],
    }),

    deleteCallTopic: build.mutation<void, TaskCtx & { id: string }>({
      async queryFn({ uid, projectId, sprintId, id }) {
        try {
          await deleteDoc(
            doc(db, `${paths.callTopics(uid, projectId, sprintId)}/${id}`),
          );
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteCallTopic failed' };
        }
      },
      invalidatesTags: [{ type: 'CallTopic', id: 'LIST' }],
    }),

    // =================================================================
    // Refinements
    // =================================================================
    listRefinements: build.query<Refinement[], TaskCtx>({
      async queryFn({ uid, projectId, sprintId }) {
        try {
          const snap = await getDocs(
            collection(db, paths.refinements(uid, projectId, sprintId)),
          );
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                sprintId,
                ...data,
                createdDate: tsToMs(data.createdDate),
              } as Refinement;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listRefinements failed' };
        }
      },
      providesTags: [{ type: 'Refinement', id: 'LIST' }],
    }),

    upsertRefinement: build.mutation<
      Refinement,
      TaskCtx & Partial<Refinement> & { topic: string }
    >({
      async queryFn({ uid, projectId, sprintId, id, ...rest }) {
        try {
          if (id) {
            await setDoc(
              doc(db, `${paths.refinements(uid, projectId, sprintId)}/${id}`),
              rest as any,
              { merge: true },
            );
            return { data: { id, sprintId, ...(rest as any), createdDate: Date.now() } as Refinement };
          } else {
            const ref = await addDoc(
              collection(db, paths.refinements(uid, projectId, sprintId)),
              { ...rest, createdDate: serverTimestamp() } as any,
            );
            return { data: { id: ref.id, sprintId, ...(rest as any), createdDate: Date.now() } as Refinement };
          }
        } catch (e: any) {
          return { error: e?.message ?? 'upsertRefinement failed' };
        }
      },
      invalidatesTags: [{ type: 'Refinement', id: 'LIST' }],
    }),

    deleteRefinement: build.mutation<void, TaskCtx & { id: string }>({
      async queryFn({ uid, projectId, sprintId, id }) {
        try {
          await deleteDoc(
            doc(db, `${paths.refinements(uid, projectId, sprintId)}/${id}`),
          );
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteRefinement failed' };
        }
      },
      invalidatesTags: [{ type: 'Refinement', id: 'LIST' }],
    }),

    // =================================================================
    // Scope items
    // =================================================================
    listScopeItems: build.query<ScopeItem[], TaskCtx>({
      async queryFn({ uid, projectId, sprintId }) {
        try {
          const snap = await getDocs(
            collection(db, paths.scopeItems(uid, projectId, sprintId)),
          );
          return {
            data: snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                sprintId,
                ...data,
                createdDate: tsToMs(data.createdDate),
              } as ScopeItem;
            }),
          };
        } catch (e: any) {
          return { error: e?.message ?? 'listScopeItems failed' };
        }
      },
      providesTags: [{ type: 'ScopeItem', id: 'LIST' }],
    }),

    upsertScopeItem: build.mutation<
      ScopeItem,
      TaskCtx & Partial<ScopeItem> & { content: string }
    >({
      async queryFn({ uid, projectId, sprintId, id, ...rest }) {
        try {
          if (id) {
            await setDoc(
              doc(db, `${paths.scopeItems(uid, projectId, sprintId)}/${id}`),
              rest as any,
              { merge: true },
            );
            return { data: { id, sprintId, ...(rest as any), createdDate: Date.now() } as ScopeItem };
          } else {
            const ref = await addDoc(
              collection(db, paths.scopeItems(uid, projectId, sprintId)),
              { ...rest, createdDate: serverTimestamp() } as any,
            );
            return { data: { id: ref.id, sprintId, ...(rest as any), createdDate: Date.now() } as ScopeItem };
          }
        } catch (e: any) {
          return { error: e?.message ?? 'upsertScopeItem failed' };
        }
      },
      invalidatesTags: [{ type: 'ScopeItem', id: 'LIST' }],
    }),

    deleteScopeItem: build.mutation<void, TaskCtx & { id: string }>({
      async queryFn({ uid, projectId, sprintId, id }) {
        try {
          await deleteDoc(
            doc(db, `${paths.scopeItems(uid, projectId, sprintId)}/${id}`),
          );
          return { data: undefined };
        } catch (e: any) {
          return { error: e?.message ?? 'deleteScopeItem failed' };
        }
      },
      invalidatesTags: [{ type: 'ScopeItem', id: 'LIST' }],
    }),
  }),
});

export const {
  useListProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useListSprintsQuery,
  useCreateSprintMutation,
  useUpdateSprintMutation,
  useDeleteSprintMutation,
  useListTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useBulkImportTasksMutation,
  useListAssigneesQuery,
  useUpsertAssigneeMutation,
  useDeleteAssigneeMutation,
  useListDailyGoalsQuery,
  useUpsertDailyGoalMutation,
  useDeleteDailyGoalMutation,
  useListCallTopicsQuery,
  useUpsertCallTopicMutation,
  useDeleteCallTopicMutation,
  useListRefinementsQuery,
  useUpsertRefinementMutation,
  useDeleteRefinementMutation,
  useListScopeItemsQuery,
  useUpsertScopeItemMutation,
  useDeleteScopeItemMutation,
} = firestoreApi;
